<!-- Header -->
<div class="d-flex flex-wrap flex-stack mb-6">
  <div>
    <h3 class="fw-bold my-2">{{ 'companyProfile.users.title' | translate }}</h3>
    <p class="text-muted fs-6 mb-0">{{ 'companyProfile.users.subtitle' | translate }}</p>
  </div>
  @if (isAdmin()) {
    <div class="d-flex flex-wrap my-2">
      <app-button
        [variant]="'primary'"
        [label]="'companyProfile.users.inviteUser' | translate"
        [iconClass]="'fa fa-user-plus'"
        (clicked)="openInviteUserModal()"
      />
    </div>
  }
</div>

<!-- Loading State -->
@if (loading) {
  <div class="card mb-9">
    <div class="card-body text-center py-15">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'companyProfile.users.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'companyProfile.users.loading' | translate }}</p>
    </div>
  </div>
}

<!-- Empty State -->
@if (!loading && users.length === 0) {
  <div class="card mb-9">
    <div class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-solid fa-users"></i>
      </div>
      <h4>{{ 'companyProfile.users.noUsers' | translate }}</h4>
      <p>{{ 'companyProfile.users.subtitle' | translate }}</p>
      @if (isAdmin()) {
        <app-button
          [variant]="'primary'"
          [label]="'companyProfile.users.inviteUser' | translate"
          [iconClass]="'fa fa-user-plus'"
          (clicked)="openInviteUserModal()"
        />
      }
    </div>
  </div>
}

<!-- Users Table -->
@if (!loading && users.length > 0) {
  <div class="card mb-9">
    <div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 mb-0">
          <thead>
            <tr class="fw-bold text-muted bg-light">
              <th class="ps-4 min-w-250px rounded-start">{{ 'companyProfile.users.name' | translate }}</th>
              <th class="min-w-150px">{{ 'companyProfile.users.email' | translate }}</th>
              <th class="min-w-100px">{{ 'companyProfile.edit.phone' | translate }}</th>
              <th class="min-w-100px">{{ 'companyProfile.users.role' | translate }}</th>
              <th class="min-w-80px">{{ 'companyProfile.users.status' | translate }}</th>
              <th class="min-w-100px text-end rounded-end pe-4">{{ 'companyProfile.users.actions' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (user of users; track user.user_id) {
              <tr>
                <!-- User Info -->
                <td class="ps-4">
                  <div class="d-flex align-items-center">
                    <div class="symbol symbol-45px me-4">
                      @if (user.profile_image_url) {
                        <img [src]="user.profile_image_url" alt="User" class="symbol-label"/>
                      } @else {
                        <div class="symbol-label bg-light-primary text-primary fw-bold">
                          {{ getInitials(user.first_name, user.last_name) }}
                        </div>
                      }
                    </div>
                    <div class="d-flex flex-column">
                      <span class="text-gray-800 fw-bold fs-6">
                        {{ user.first_name }} {{ user.last_name }}
                      </span>
                      @if (user.identificator) {
                        <span class="text-muted fs-7">ID: {{ user.identificator }}</span>
                      }
                    </div>
                  </div>
                </td>

                <!-- Email -->
                <td>
                  <span class="text-gray-600 fs-7">{{ user.email || '-' }}</span>
                </td>

                <!-- Phone -->
                <td>
                  <span class="text-gray-600 fs-7">{{ user.phone || '-' }}</span>
                </td>

                <!-- Role -->
                <td>
                  @if (isAdmin()) {
                    <app-selectorini
                      [options]="roleOptions"
                      [displayKey]="'role_name'"
                      [valueKey]="'role_id'"
                      [value]="user.role_id"
                      (valueChange)="updateUserRole(user, $event)"
                    />
                  } @else {
                    <span class="badge" [class.badge-primary]="user.role_id === 1" [class.badge-secondary]="user.role_id === 2">
                      {{ user.role_id === 1 ? ('companyProfile.users.roleAdmin' | translate) : ('companyProfile.users.roleMember' | translate) }}
                    </span>
                  }
                </td>

                <!-- Status -->
                <td>
                  @if (user.active === 1) {
                    <span class="badge badge-light-success">{{ 'companyProfile.users.active' | translate }}</span>
                  } @else {
                    <span class="badge badge-light-warning">{{ 'companyProfile.users.pending' | translate }}</span>
                  }
                </td>

                <!-- Actions -->
                <td class="text-end pe-4">
                  @if (isAdmin()) {
                    <button
                      class="btn btn-sm btn-icon btn-light-danger"
                      (click)="removeUser(user)"
                      [title]="'companyProfile.users.removeUser' | translate"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
}
